import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { newsData, categoryColors } from '../../../data/newsData';
import type { NewsItem } from '../../../data/newsData';

const categories = ['すべて', 'プレスリリース', 'お知らせ', 'イベント'];

export default function News() {
  const [activeCategory, setActiveCategory] = useState('すべて');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  const filtered = activeCategory === 'すべて'
    ? newsData
    : newsData.filter((n) => n.category === activeCategory);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <section id="news" className="py-16 md:py-28 bg-[#F9F7F4]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-12 gap-6"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">News & Notice</span>
              <div className="w-8 h-px bg-[#B8960C]"></div>
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              ニュース・お知らせ
            </h2>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-3 md:px-4 py-2 text-sm tracking-wider font-medium transition-all cursor-pointer whitespace-nowrap rounded-sm border min-h-[44px] ${
                  activeCategory === cat
                    ? 'bg-[#B8960C] text-white border-[#B8960C]'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-[#B8960C] hover:text-[#B8960C]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400 text-sm">該当するニュースはありません。</div>
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 md:gap-8">
            {/* Featured Article */}
            {featured && <FeaturedCard news={featured} />}

            {/* Side List */}
            <div className="lg:col-span-2 flex flex-col gap-4 md:gap-5">
              {rest.map((item, i) => (
                <SideCard key={item.id} news={item} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* View All */}
        <div
          className="mt-10 md:mt-12 text-center"
          style={{
            opacity: headerVisible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.4s',
          }}
        >
          <a
            href="#news"
            className="inline-flex items-center gap-3 border border-[#B8960C] text-[#B8960C] px-8 md:px-10 py-3.5 text-sm font-medium tracking-widest hover:bg-[#B8960C] hover:text-white transition-all cursor-pointer whitespace-nowrap min-h-[44px]"
          >
            ニュース一覧を見る
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function FeaturedCard({ news }: { news: NewsItem }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <Link
      to={`/news/${news.id}`}
      ref={ref as React.RefObject<HTMLAnchorElement>}
      className="lg:col-span-1 group cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(36px)',
        transition: 'opacity 0.7s ease, transform 0.7s ease',
      }}
    >
      <div className="bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
        <div className="relative w-full h-48 md:h-56 overflow-hidden flex-shrink-0">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {news.tag === 'hot' && (
            <div className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2.5 py-1 font-medium tracking-wider">
              NEW
            </div>
          )}
          {news.tag === 'award' && (
            <div className="absolute top-3 left-3 bg-[#B8960C] text-white text-xs px-2.5 py-1 font-medium tracking-wider flex items-center gap-1">
              <i className="ri-award-line text-xs"></i> 受賞
            </div>
          )}
        </div>
        <div className="p-5 md:p-6 flex flex-col flex-1">
          <div className="flex items-center gap-2 md:gap-3 mb-3">
            <span className={`text-xs md:text-sm px-2 md:px-2.5 py-1 font-medium rounded-sm ${categoryColors[news.category] || 'bg-gray-100 text-gray-600'}`}>
              {news.category}
            </span>
            <span className="text-gray-400 text-xs md:text-sm">{news.date}</span>
          </div>
          <h3
            className="text-base md:text-lg font-bold text-[#1A1A1A] mb-2 md:mb-3 leading-snug group-hover:text-[#B8960C] transition-colors"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            {news.title}
          </h3>
          <p className="text-gray-500 text-sm md:text-base leading-relaxed flex-1">{news.summary}</p>
          <div className="mt-3 md:mt-4 flex items-center gap-2 text-[#B8960C] text-sm md:text-base font-medium">
            続きを読む
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

function SideCard({ news, index }: { news: NewsItem; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <Link
      to={`/news/${news.id}`}
      ref={ref as React.RefObject<HTMLAnchorElement>}
      className="group cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(32px)',
        transition: `opacity 0.6s ease ${index * 0.08}s, transform 0.6s ease ${index * 0.08}s`,
      }}
    >
      <div className="bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex gap-0">
        <div className="w-28 md:w-36 h-24 md:h-28 flex-shrink-0 overflow-hidden">
          <img
            src={news.image}
            alt={news.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-3 md:p-4 flex flex-col justify-between flex-1 min-w-0">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-xs md:text-sm px-1.5 md:px-2 py-0.5 font-medium rounded-sm ${categoryColors[news.category] || 'bg-gray-100 text-gray-600'}`}>
                {news.category}
              </span>
              <span className="text-gray-400 text-xs md:text-sm">{news.date}</span>
            </div>
            <h3
              className="text-sm md:text-base font-bold text-[#1A1A1A] leading-snug group-hover:text-[#B8960C] transition-colors line-clamp-2"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {news.title}
            </h3>
          </div>
          <div className="flex items-center gap-1 text-[#B8960C] text-xs md:text-sm font-medium mt-2">
            詳細を見る
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}